"use client";

import React, { createContext, useContext, useMemo, useRef, useState, useEffect } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { LOCALE_COOKIE, type Locale } from "./config";
import type { Messages } from "./messages";
import { translate } from "./translate";
import { loadMessages } from "./messages";
import {
  buildPathWithSearch,
  getLocaleFromPathname,
  isSupportedLocale,
  stripLocaleFromPathname,
  withLocale,
} from "./utils";

type I18nContextValue = {
  locale: Locale;
  messages: Messages;
  t: (key: string, vars?: Record<string, string | number>) => string;
  setLocale: (nextLocale: Locale) => Promise<void>;
};

const I18nContext = createContext<I18nContextValue | null>(null);

const readLocaleCookie = (): Locale | null => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${LOCALE_COOKIE}=([^;]*)`)
  );
  if (!match) return null;
  const value = decodeURIComponent(match[1] || "");
  return isSupportedLocale(value) ? (value as Locale) : null;
};

export const I18nProvider = ({
  locale,
  messages,
  children,
}: {
  locale: Locale;
  messages: Messages;
  children: React.ReactNode;
}) => {
  const [activeLocale, setActiveLocale] = useState(locale);
  const [activeMessages, setActiveMessages] = useState(messages);
  const [isLocaleLoading, setIsLocaleLoading] = useState(false);
  const requestIdRef = useRef(0);
  const previousMessagesRef = useRef(messages);
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathnameLocale = getLocaleFromPathname(pathname);
  const normalizedPath = useMemo(() => {
    return stripLocaleFromPathname(pathname, pathnameLocale);
  }, [pathname, pathnameLocale]);
  const search = useMemo(() => searchParams.toString(), [searchParams]);

  useEffect(() => {
    if (pathnameLocale && pathnameLocale !== locale) {
      return;
    }
    setActiveLocale(locale);
    setActiveMessages(messages);
    previousMessagesRef.current = messages;
  }, [locale, messages, pathnameLocale]);

  useEffect(() => {
    if (!pathnameLocale || pathnameLocale === activeLocale) return;
    const preferredLocale = readLocaleCookie();
    if (preferredLocale && preferredLocale !== pathnameLocale) {
      const nextPath = withLocale(normalizedPath, preferredLocale);
      const nextHref = buildPathWithSearch(nextPath, search);
      router.replace(nextHref, { scroll: false });
      return;
    }
    const requestId = ++requestIdRef.current;
    setIsLocaleLoading(true);
    loadMessages(pathnameLocale)
      .then((nextMessages) => {
        if (requestId !== requestIdRef.current) return;
        setActiveLocale(pathnameLocale);
        setActiveMessages(nextMessages);
        previousMessagesRef.current = nextMessages;
        setIsLocaleLoading(false);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        setIsLocaleLoading(false);
      });
  }, [pathnameLocale, activeLocale, normalizedPath, router, search]);

  useEffect(() => {
    try {
      document.documentElement.lang = activeLocale;
    } catch {}
  }, [activeLocale]);

  const setLocale = async (nextLocale: Locale) => {
    if (nextLocale === activeLocale) return;
    const requestId = ++requestIdRef.current;
    setIsLocaleLoading(true);
    try {
      const nextMessages = await loadMessages(nextLocale);
      if (requestId !== requestIdRef.current) return;
      setActiveLocale(nextLocale);
      setActiveMessages(nextMessages);
      previousMessagesRef.current = nextMessages;
      setIsLocaleLoading(false);
    } catch {
      if (requestId !== requestIdRef.current) return;
      setIsLocaleLoading(false);
    }
  };

  const t = useMemo(() => {
    return (key: string, vars?: Record<string, string | number>) => {
      const primary = translate(activeMessages, key, vars, activeLocale);
      if (!isLocaleLoading || primary !== key) {
        return primary;
      }
      return translate(previousMessagesRef.current, key, vars, activeLocale);
    };
  }, [activeMessages, activeLocale, isLocaleLoading]);

  return (
    <I18nContext.Provider
      value={{ locale: activeLocale, messages: activeMessages, t, setLocale }}
    >
      {children}
    </I18nContext.Provider>
  );
};

export const useTranslations = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useTranslations must be used within I18nProvider");
  }
  return context.t;
};

export const useLocale = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useLocale must be used within I18nProvider");
  }
  return context.locale;
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within I18nProvider");
  }
  return context;
};
